/**
 * New Design Change entry point — guided kind picker and per-kind op form.
 * Screen 8 of the atlas-v2 mockup: choose one of 9 object kinds, fill in
 * required fields, optionally use "describe it" heuristic kind-guesser,
 * then submit to create a sandbox op.
 */

import { useCallback, useState } from 'react';
import type { AtlasManifest, CloudFunctionKind, EventSafetyTier, ReferenceKind, SurfaceType } from '@atlas-manifest/types';
import type { SandboxOp } from '../state/sandboxOps.js';
import { validateOp } from '../state/sandboxOps.js';
import { guessKind } from '../state/kindGuesser.js';
import { useAiSuggest, parseAiOpsResponse } from '../state/useAiSuggest.js';
import { describeDesignChangePrompt, type AiSuggestOptions } from '../state/aiPrompts.js';
import type { AskAtlasResult } from '../state/atlasApiTypes.js';
import { streamAskAtlas } from '../firebase/atlasApi.js';
import { Icon } from '../components/Icon.js';
import './NewDesignChangeView.css';

export interface NewDesignChangeViewProps {
  manifest: AtlasManifest;
  onSubmit: (op: SandboxOp) => void;
  onCancel: () => void;
  /** Injected in tests; defaults to streamAskAtlas. */
  onAsk?: (opts: AiSuggestOptions) => Promise<AskAtlasResult>;
}

interface OpDraft {
  // Entity fields
  entityId: string;
  entityName: string;
  entityDomain: string;
  entityDescription: string;

  // Domain fields
  domainId: string;
  domainName: string;
  domainColor: string;
  domainDescription: string;

  // Reference fields
  refFromEntity: string;
  refToEntity: string;
  refFromField: string;
  refKind: string;

  // Surface fields
  surfaceId: string;
  surfaceType: string;
  surfaceDomain: string;
  surfaceHub: string;

  // Cloud Function fields
  functionName: string;
  functionKind: string;
  functionDomain: string;

  // Event fields
  eventType: string;
  eventCategory: string;
  eventSafetyTier: string;

  // Path fields
  pathEntityId: string;
  pathPath: string;
  pathRetention: string;

  // Tag fields
  tagTag: string;
  tagDescription: string;

  // State Machine fields
  machineId: string;
  machineName: string;
  machineDomain: string;
}

const EMPTY_DRAFT: OpDraft = {
  entityId: '',
  entityName: '',
  entityDomain: '',
  entityDescription: '',
  domainId: '',
  domainName: '',
  domainColor: '',
  domainDescription: '',
  refFromEntity: '',
  refToEntity: '',
  refFromField: '',
  refKind: 'fk',
  surfaceId: '',
  surfaceType: 'card',
  surfaceDomain: '',
  surfaceHub: '',
  functionName: '',
  functionKind: 'callable',
  functionDomain: '',
  eventType: '',
  eventCategory: '',
  eventSafetyTier: '2',
  pathEntityId: '',
  pathPath: '',
  pathRetention: '',
  tagTag: '',
  tagDescription: '',
  machineId: '',
  machineName: '',
  machineDomain: '',
};

type CreateKind = Extract<SandboxOp['kind'], `create-${string}`>;

interface KindMeta {
  kind: CreateKind;
  title: string;
  description: string;
  icon: string;
}

const KINDS: readonly KindMeta[] = [
  {
    kind: 'create-entity',
    title: 'Entity',
    description: 'A domain object with fields and relationships',
    icon: 'category',
  },
  {
    kind: 'create-domain',
    title: 'Domain',
    description: 'Organize related entities together',
    icon: 'hub',
  },
  {
    kind: 'create-reference',
    title: 'Reference',
    description: 'Link between two entities',
    icon: 'link',
  },
  {
    kind: 'create-surface',
    title: 'Surface',
    description: 'UI component rendered to users',
    icon: 'widgets',
  },
  {
    kind: 'create-cloud-function',
    title: 'Cloud Function',
    description: 'Service handler or trigger',
    icon: 'functions',
  },
  {
    kind: 'create-event',
    title: 'Event',
    description: 'Domain event emitted when something happens',
    icon: 'bolt',
  },
  {
    kind: 'create-path',
    title: 'Path',
    description: 'Firestore collection/document path',
    icon: 'folder_open',
  },
  {
    kind: 'create-tag',
    title: 'Tag',
    description: 'Metadata label for classification',
    icon: 'label',
  },
  {
    kind: 'create-state-machine',
    title: 'State Machine',
    description: 'State workflow with transitions',
    icon: 'account_tree',
  },
];

/** Icon names above are real Material Symbols Rounded ligatures (verified
 *  against this codebase's existing usages in icons/catalog.ts, several
 *  reused directly: 'hub' is domainIcon()'s own fallback, 'account_tree'
 *  is ENTITY_SHAPE_ICONS.fsm, 'label' is SURFACE_ICONS.chip). */

function countKind(manifest: AtlasManifest, kind: CreateKind): number {
  switch (kind) {
    case 'create-entity':
      return manifest.entities.length;
    case 'create-domain':
      return manifest.domains.length;
    case 'create-reference':
      return manifest.references.length;
    case 'create-surface':
      return manifest.surfaces.length;
    case 'create-cloud-function':
      return manifest.cloudFunctions.length;
    case 'create-event':
      return manifest.events.length;
    case 'create-path':
      return manifest.paths.length;
    case 'create-tag':
      return manifest.tags.length;
    case 'create-state-machine':
      return manifest.stateMachines.length;
  }
}

function draftToOp(draft: OpDraft, kind: CreateKind): SandboxOp | null {
  switch (kind) {
    case 'create-entity':
      return draft.entityId && draft.entityName && draft.entityDomain && draft.entityDescription
        ? {
            kind: 'create-entity',
            entityId: draft.entityId,
            name: draft.entityName,
            domain: draft.entityDomain,
            description: draft.entityDescription,
          }
        : null;
    case 'create-domain':
      return draft.domainId && draft.domainName && draft.domainColor && draft.domainDescription
        ? {
            kind: 'create-domain',
            domainId: draft.domainId,
            name: draft.domainName,
            color: draft.domainColor,
            description: draft.domainDescription,
          }
        : null;
    case 'create-reference':
      return draft.refFromEntity && draft.refToEntity && draft.refFromField && draft.refKind
        ? {
            kind: 'create-reference',
            from: draft.refFromEntity,
            to: draft.refToEntity,
            fromField: draft.refFromField,
            referenceKind: draft.refKind as ReferenceKind,
          }
        : null;
    case 'create-surface':
      return draft.surfaceId && draft.surfaceType && draft.surfaceDomain && draft.surfaceHub
        ? {
            kind: 'create-surface',
            surfaceId: draft.surfaceId,
            surfaceType: draft.surfaceType as SurfaceType,
            domain: draft.surfaceDomain,
            hub: draft.surfaceHub,
          }
        : null;
    case 'create-cloud-function':
      return draft.functionName && draft.functionKind && draft.functionDomain
        ? {
            kind: 'create-cloud-function',
            name: draft.functionName,
            functionKind: draft.functionKind as CloudFunctionKind,
            domain: draft.functionDomain,
          }
        : null;
    case 'create-event':
      return draft.eventType && draft.eventCategory && draft.eventSafetyTier
        ? {
            kind: 'create-event',
            eventType: draft.eventType,
            category: draft.eventCategory,
            safetyTier: Number(draft.eventSafetyTier) as EventSafetyTier,
          }
        : null;
    case 'create-path':
      return draft.pathEntityId && draft.pathPath && draft.pathRetention
        ? {
            kind: 'create-path',
            entityId: draft.pathEntityId,
            path: draft.pathPath,
            retention: draft.pathRetention,
          }
        : null;
    case 'create-tag':
      return draft.tagTag && draft.tagDescription
        ? {
            kind: 'create-tag',
            tag: draft.tagTag,
            description: draft.tagDescription,
          }
        : null;
    case 'create-state-machine':
      return draft.machineId && draft.machineName && draft.machineDomain
        ? {
            kind: 'create-state-machine',
            machineId: draft.machineId,
            name: draft.machineName,
            domain: draft.machineDomain,
          }
        : null;
  }
}

/** Inverse of draftToOp — pre-fills the per-kind form from an AI-proposed op. */
function opToDraft(op: SandboxOp): OpDraft {
  switch (op.kind) {
    case 'create-entity':
      return { ...EMPTY_DRAFT, entityId: op.entityId, entityName: op.name, entityDomain: op.domain, entityDescription: op.description };
    case 'create-domain':
      return { ...EMPTY_DRAFT, domainId: op.domainId, domainName: op.name, domainColor: op.color, domainDescription: op.description };
    case 'create-reference':
      return { ...EMPTY_DRAFT, refFromEntity: op.from, refToEntity: op.to, refFromField: op.fromField, refKind: op.referenceKind };
    case 'create-surface':
      return { ...EMPTY_DRAFT, surfaceId: op.surfaceId, surfaceType: op.surfaceType, surfaceDomain: op.domain, surfaceHub: op.hub };
    case 'create-cloud-function':
      return { ...EMPTY_DRAFT, functionName: op.name, functionKind: op.functionKind, functionDomain: op.domain };
    case 'create-event':
      return { ...EMPTY_DRAFT, eventType: op.eventType, eventCategory: op.category, eventSafetyTier: String(op.safetyTier) };
    case 'create-path':
      return { ...EMPTY_DRAFT, pathEntityId: op.entityId, pathPath: op.path, pathRetention: op.retention };
    case 'create-tag':
      return { ...EMPTY_DRAFT, tagTag: op.tag, tagDescription: op.description };
    case 'create-state-machine':
      return { ...EMPTY_DRAFT, machineId: op.machineId, machineName: op.name, machineDomain: op.domain };
    default:
      return EMPTY_DRAFT;
  }
}

/** Where the raw description text lands when the keyword-heuristic fallback picks a kind. */
function descriptionFieldFor(kind: CreateKind): keyof OpDraft | null {
  switch (kind) {
    case 'create-entity':
      return 'entityDescription';
    case 'create-domain':
      return 'domainDescription';
    case 'create-tag':
      return 'tagDescription';
    default:
      return null;
  }
}

export function NewDesignChangeView({
  manifest,
  onSubmit,
  onCancel,
  onAsk,
}: NewDesignChangeViewProps): React.JSX.Element {
  const [selectedKind, setSelectedKind] = useState<CreateKind | null>(null);
  const [draft, setDraft] = useState<OpDraft>(EMPTY_DRAFT);
  const [description, setDescription] = useState('');

  const defaultOnAsk = useCallback(
    (opts: AiSuggestOptions) => streamAskAtlas(opts.prompt, opts.summary, opts.knownEntityIds),
    [],
  );
  const ai = useAiSuggest(onAsk ?? defaultOnAsk);

  const handleKindClick = useCallback((kind: CreateKind) => {
    setSelectedKind(kind);
    setDraft(EMPTY_DRAFT);
    setDescription('');
  }, []);

  // Typing only updates local text — it must NEVER switch views on its own.
  // guessKind() never returns null for non-empty text (it defaults to
  // 'create-entity'), so doing the switch here would unmount this very box
  // after the first keystroke, before the user can finish describing anything.
  const handleDescriptionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDescription(e.target.value);
    },
    [],
  );

  // Explicit trigger only (button click). Tries the real Ask Atlas AI first —
  // parses its response into a full proposed op via parseAiOpsResponse — and
  // falls back to the local keyword heuristic if AI is unavailable or returns
  // nothing usable, so the flow always works even with AI disabled/erroring.
  const handleAskAtlas = useCallback(async () => {
    const trimmed = description.trim();
    if (!trimmed) return;

    const result = await ai.suggest(describeDesignChangePrompt(trimmed, manifest));
    const entries = result ? parseAiOpsResponse(result.answer) : [];
    if (entries.length > 0) {
      const proposedOp = entries[0]!.op;
      setSelectedKind(proposedOp.kind as CreateKind);
      setDraft(opToDraft(proposedOp));
      return;
    }

    const guess = guessKind(trimmed) ?? 'create-entity';
    const field = descriptionFieldFor(guess);
    setSelectedKind(guess);
    setDraft(field ? { ...EMPTY_DRAFT, [field]: trimmed } : EMPTY_DRAFT);
  }, [description, ai, manifest]);

  const handleDraftChange = useCallback((field: keyof OpDraft, value: string | boolean) => {
    setDraft((d) => ({ ...d, [field]: value }));
  }, []);

  const handleSubmit = useCallback(() => {
    if (!selectedKind) return;
    const op = draftToOp(draft, selectedKind);
    if (op && validateOp(op).ok) {
      onSubmit(op);
      setSelectedKind(null);
      setDraft(EMPTY_DRAFT);
      setDescription('');
    }
  }, [selectedKind, draft, onSubmit]);

  const handleCancel = useCallback(() => {
    setSelectedKind(null);
    setDraft(EMPTY_DRAFT);
    setDescription('');
  }, []);

  const op = selectedKind ? draftToOp(draft, selectedKind) : null;
  const canSubmit = op !== null && validateOp(op).ok;

  if (selectedKind) {
    // Form view for the selected kind.
    const meta = KINDS.find((k) => k.kind === selectedKind)!;
    return (
      <div className="new-design-change-form">
        <div className="form-header">
          <h2>{meta.title}</h2>
          <button className="btn-close" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>

        {selectedKind === 'create-entity' && (
          <fieldset className="new-design-change-fields">
            <label>
              Entity ID
              <input
                type="text"
                placeholder="e.g., Holding"
                value={draft.entityId}
                onChange={(e) => handleDraftChange('entityId', e.target.value)}
              />
            </label>
            <label>
              Display Name
              <input
                type="text"
                placeholder="e.g., Investment Holding"
                value={draft.entityName}
                onChange={(e) => handleDraftChange('entityName', e.target.value)}
              />
            </label>
            <label>
              Domain
              <select
                value={draft.entityDomain}
                onChange={(e) => handleDraftChange('entityDomain', e.target.value)}
              >
                <option value="">Select Domain</option>
                {manifest.domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Description
              <textarea
                placeholder="e.g., A domain object"
                value={draft.entityDescription}
                onChange={(e) => handleDraftChange('entityDescription', e.target.value)}
              />
            </label>
          </fieldset>
        )}

        {selectedKind === 'create-domain' && (
          <fieldset className="new-design-change-fields">
            <label>
              Domain ID
              <input
                type="text"
                placeholder="e.g., investments"
                value={draft.domainId}
                onChange={(e) => handleDraftChange('domainId', e.target.value)}
              />
            </label>
            <label>
              Display Name
              <input
                type="text"
                placeholder="e.g., Investments"
                value={draft.domainName}
                onChange={(e) => handleDraftChange('domainName', e.target.value)}
              />
            </label>
            <label>
              Color
              <input
                type="text"
                placeholder="e.g., --d-invest"
                value={draft.domainColor}
                onChange={(e) => handleDraftChange('domainColor', e.target.value)}
              />
            </label>
            <label>
              Description
              <textarea
                placeholder="e.g., Organize related entities"
                value={draft.domainDescription}
                onChange={(e) => handleDraftChange('domainDescription', e.target.value)}
              />
            </label>
          </fieldset>
        )}

        {selectedKind === 'create-reference' && (
          <fieldset className="new-design-change-fields">
            <label>
              From Entity
              <input
                type="text"
                placeholder="e.g., Holding"
                value={draft.refFromEntity}
                onChange={(e) => handleDraftChange('refFromEntity', e.target.value)}
              />
            </label>
            <label>
              To Entity
              <input
                type="text"
                placeholder="e.g., Account"
                value={draft.refToEntity}
                onChange={(e) => handleDraftChange('refToEntity', e.target.value)}
              />
            </label>
            <label>
              From Field
              <input
                type="text"
                placeholder="e.g., accountId"
                value={draft.refFromField}
                onChange={(e) => handleDraftChange('refFromField', e.target.value)}
              />
            </label>
            <label>
              Reference Kind
              <select
                value={draft.refKind}
                onChange={(e) => handleDraftChange('refKind', e.target.value)}
              >
                <option value="fk">Foreign Key</option>
                <option value="embed">Embed</option>
                <option value="enum">Enum</option>
                <option value="aggregate">Aggregate</option>
                <option value="event">Event</option>
              </select>
            </label>
          </fieldset>
        )}

        {selectedKind === 'create-surface' && (
          <fieldset className="new-design-change-fields">
            <label>
              Surface ID
              <input
                type="text"
                placeholder="e.g., investment_summary"
                value={draft.surfaceId}
                onChange={(e) => handleDraftChange('surfaceId', e.target.value)}
              />
            </label>
            <label>
              Surface Type
              <select
                value={draft.surfaceType}
                onChange={(e) => handleDraftChange('surfaceType', e.target.value)}
              >
                <option value="card">Card</option>
                <option value="chart">Chart</option>
                <option value="overlay">Overlay</option>
                <option value="chip">Chip</option>
                <option value="ai">AI</option>
                <option value="hero">Hero</option>
                <option value="list">List</option>
                <option value="sheet">Sheet</option>
                <option value="banner">Banner</option>
                <option value="grid">Grid</option>
                <option value="other">Other</option>
              </select>
            </label>
            <label>
              Domain
              <select
                value={draft.surfaceDomain}
                onChange={(e) => handleDraftChange('surfaceDomain', e.target.value)}
              >
                <option value="">Select Domain</option>
                {manifest.domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Hub
              <input
                type="text"
                placeholder="e.g., money"
                value={draft.surfaceHub}
                onChange={(e) => handleDraftChange('surfaceHub', e.target.value)}
              />
            </label>
          </fieldset>
        )}

        {selectedKind === 'create-cloud-function' && (
          <fieldset className="new-design-change-fields">
            <label>
              Function Name
              <input
                type="text"
                placeholder="e.g., processTransaction"
                value={draft.functionName}
                onChange={(e) => handleDraftChange('functionName', e.target.value)}
              />
            </label>
            <label>
              Function Kind
              <select
                value={draft.functionKind}
                onChange={(e) => handleDraftChange('functionKind', e.target.value)}
              >
                <option value="callable">Callable</option>
                <option value="http">HTTP</option>
                <option value="trigger">Trigger</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </label>
            <label>
              Domain
              <select
                value={draft.functionDomain}
                onChange={(e) => handleDraftChange('functionDomain', e.target.value)}
              >
                <option value="">Select Domain</option>
                {manifest.domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>
        )}

        {selectedKind === 'create-event' && (
          <fieldset className="new-design-change-fields">
            <label>
              Event Type
              <input
                type="text"
                placeholder="e.g., holding_repriced"
                value={draft.eventType}
                onChange={(e) => handleDraftChange('eventType', e.target.value)}
              />
            </label>
            <label>
              Category
              <input
                type="text"
                placeholder="e.g., portfolio"
                value={draft.eventCategory}
                onChange={(e) => handleDraftChange('eventCategory', e.target.value)}
              />
            </label>
            <label>
              Safety Tier
              <select
                value={draft.eventSafetyTier}
                onChange={(e) => handleDraftChange('eventSafetyTier', e.target.value)}
              >
                <option value="0">Tier 0 - Critical Safety</option>
                <option value="1">Tier 1 - Compliance</option>
                <option value="2">Tier 2 - Operational</option>
                <option value="3">Tier 3 - Observability</option>
              </select>
            </label>
          </fieldset>
        )}

        {selectedKind === 'create-path' && (
          <fieldset className="new-design-change-fields">
            <label>
              Entity ID
              <input
                type="text"
                placeholder="e.g., Holding"
                value={draft.pathEntityId}
                onChange={(e) => handleDraftChange('pathEntityId', e.target.value)}
              />
            </label>
            <label>
              Firestore Path
              <input
                type="text"
                placeholder="e.g., /users/{uid}/holdings"
                value={draft.pathPath}
                onChange={(e) => handleDraftChange('pathPath', e.target.value)}
              />
            </label>
            <label>
              Retention
              <input
                type="text"
                placeholder="e.g., 7y · audit"
                value={draft.pathRetention}
                onChange={(e) => handleDraftChange('pathRetention', e.target.value)}
              />
            </label>
          </fieldset>
        )}

        {selectedKind === 'create-tag' && (
          <fieldset className="new-design-change-fields">
            <label>
              Tag
              <input
                type="text"
                placeholder="e.g., tier:premium"
                value={draft.tagTag}
                onChange={(e) => handleDraftChange('tagTag', e.target.value)}
              />
            </label>
            <label>
              Description
              <textarea
                placeholder="e.g., Metadata label"
                value={draft.tagDescription}
                onChange={(e) => handleDraftChange('tagDescription', e.target.value)}
              />
            </label>
          </fieldset>
        )}

        {selectedKind === 'create-state-machine' && (
          <fieldset className="new-design-change-fields">
            <label>
              Machine ID
              <input
                type="text"
                placeholder="e.g., checkout"
                value={draft.machineId}
                onChange={(e) => handleDraftChange('machineId', e.target.value)}
              />
            </label>
            <label>
              Name
              <input
                type="text"
                placeholder="e.g., Checkout Flow"
                value={draft.machineName}
                onChange={(e) => handleDraftChange('machineName', e.target.value)}
              />
            </label>
            <label>
              Domain
              <select
                value={draft.machineDomain}
                onChange={(e) => handleDraftChange('machineDomain', e.target.value)}
              >
                <option value="">Select Domain</option>
                {manifest.domains.map((d) => (
                  <option key={d.id} value={d.id}>
                    {d.name}
                  </option>
                ))}
              </select>
            </label>
          </fieldset>
        )}

        <div className="form-actions">
          <button onClick={handleCancel} className="btn btn-ghost">
            Cancel
          </button>
          <button onClick={handleSubmit} className="btn btn-primary" disabled={!canSubmit}>
            Submit
          </button>
        </div>
      </div>
    );
  }

  // Grid view.
  return (
    <div className="new-design-change-grid">
      <div className="grid-header">
        <h2>Create a New Design Change</h2>
        <button className="btn-close" onClick={onCancel} aria-label="Close">
          ×
        </button>
      </div>

      <textarea
        className="describe-it-input"
        placeholder="Describe what you're building (optional). We'll help pick the right kind."
        value={description}
        onChange={handleDescriptionChange}
      />
      <div className="describe-it-actions">
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          onClick={() => void handleAskAtlas()}
          disabled={!description.trim() || ai.busy}
        >
          <Icon name="auto_awesome" size="sm" />
          Ask Atlas
        </button>
        {ai.busy && <span role="status" className="spinner" />}
      </div>

      <div className="kind-picker-grid">
        {KINDS.map((meta) => (
          <button
            key={meta.kind}
            className="kind-card"
            onClick={() => handleKindClick(meta.kind)}
            title={meta.description}
          >
            <Icon name={meta.icon} />
            <h3>{meta.title}</h3>
            <p>{meta.description}</p>
            <span className="count">{countKind(manifest, meta.kind)} existing</span>
          </button>
        ))}
      </div>
    </div>
  );
}
